import type { ComponentType, SVGProps } from 'react';
import { isFeatureListed } from '@/config/features';
import {
  HomeIcon, GlobeAltIcon, InboxIcon, AtSymbolIcon, ArrowsRightLeftIcon,
  ChartPieIcon, QueueListIcon, DocumentTextIcon, ChartBarIcon,
  HeartIcon, Cog6ToothIcon, UsersIcon, UserGroupIcon, RectangleStackIcon, BuildingOffice2Icon,
  ShieldExclamationIcon, EnvelopeIcon, FireIcon, BellAlertIcon,
  PaperClipIcon,
  LinkIcon,
  AdjustmentsHorizontalIcon,
  FunnelIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  permission?: string;
  /** Shown when the user holds ANY of these (in addition to `permission`, if set). */
  anyPermission?: string[];
  /** Shown only to platform superadmins (UI hint; the backend enforces it). */
  superadminOnly?: boolean;
  shortcut?: string;
}

export interface NavGroup {
  key: string;
  label?: string;
  items: NavItem[];
}

/** The declarative navigation model — the sidebar + command palette read this. */
export const navigation: NavGroup[] = [
  {
    key: 'main',
    items: [
      { key: 'overview', label: 'Overview', to: '/', icon: HomeIcon, shortcut: 'h' },
    ],
  },
  {

    key: 'web-hosting',

    label: 'Web Hosting',

    items: [

      {

        key: 'hosted-websites',

        label: 'Websites',

        to: '/web-hosting',

        icon: GlobeAltIcon,

        permission: 'site:read',

      },

    ],

  },

  {
    key: 'email',
    label: 'Email',
    items: [
      { key: 'domains', label: 'Domains', to: '/domains', icon: GlobeAltIcon, shortcut: 'd' },
      { key: 'mailboxes', label: 'Mailboxes', to: '/mailboxes', icon: InboxIcon, shortcut: 'm' },
      { key: 'migrations', label: 'Email migration', to: '/migrations', icon: ArrowDownTrayIcon, permission: 'mailbox:manage' },
      { key: 'aliases', label: 'Aliases', to: '/aliases', icon: AtSymbolIcon, shortcut: 'a' },
      { key: 'forwarders', label: 'Forwarders', to: '/forwarders', icon: ArrowsRightLeftIcon, shortcut: 'f' },
      { key: 'mail-tracking', label: 'Mail Tracking', to: '/mail-tracking', icon: EnvelopeIcon, shortcut: 't' },
      { key: 'threat-dashboard', label: 'Threat Dashboard', to: '/threat-dashboard', icon: FireIcon, permission: 'security.view' },
      { key: 'security-alerts', label: 'Security Alerts', to: '/security-alerts', icon: BellAlertIcon, permission: 'security.view' },
      { key: 'attachments', label: 'Attachments', to: '/attachments', icon: PaperClipIcon, permission: 'security.view' },
      { key: 'url-investigation', label: 'URL Investigation', to: '/url-investigation', icon: LinkIcon, permission: 'security.view' },
      { key: 'spam-intelligence', label: 'Spam Intelligence', to: '/spam-intelligence', icon: FunnelIcon, permission: 'security.view' },
      { key: 'queue', label: 'Mail Queue', to: '/queue', icon: QueueListIcon, permission: 'security.view' },
      { key: 'policies', label: 'Policy Engine', to: '/policies', icon: AdjustmentsHorizontalIcon, permission: 'policy.view' },
      { key: 'shared-mailboxes', label: 'Shared mailboxes', to: '/shared-mailboxes', icon: UserGroupIcon },
      { key: 'distribution-lists', label: 'Distribution lists', to: '/distribution-lists', icon: RectangleStackIcon },
      { key: 'quotas', label: 'Quotas', to: '/quotas', icon: ChartPieIcon, shortcut: 'q' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    items: [
      { key: 'logs', label: 'Logs', to: '/logs', icon: DocumentTextIcon },
      { key: 'statistics', label: 'Statistics', to: '/statistics', icon: ChartBarIcon },
      { key: 'reports', label: 'Reports', to: '/reports', icon: DocumentChartBarIcon, permission: 'reports.view' },
      { key: 'quarantine', label: 'Quarantine', to: '/quarantine', icon: ShieldExclamationIcon },
      { key: 'health', label: 'Health', to: '/health', icon: HeartIcon },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    items: [
      { key: 'onboard-organization', label: 'Onboard organization', to: '/organizations/new', icon: BuildingOffice2Icon, superadminOnly: true },
      { key: 'team', label: 'Team & organization', to: '/team', icon: UsersIcon, anyPermission: ['member:read', 'org:read'], shortcut: 'u' },
      { key: 'settings', label: 'Settings', to: '/settings/profile', icon: Cog6ToothIcon },
    ],
  },
];

/** Flat list for the command palette + breadcrumb label lookup. */
export const navFlat: NavItem[] = navigation.flatMap((g) => g.items);

export interface NavVisibilityContext {
  can: (permission: string) => boolean;
  canOnboard: boolean;
}

/**
 * Single visibility rule for the sidebar and the command palette: the user holds the item's
 * permission(s), superadmin-only items need platform onboarding rights, and features that are
 * neither available nor planned are hidden (config/features.ts). The route guard and the backend
 * remain authoritative; this only decides what the UI offers.
 */
export function isNavItemVisible(item: NavItem, ctx: NavVisibilityContext): boolean {
  if (item.permission && !ctx.can(item.permission)) return false;
  if (item.anyPermission && !item.anyPermission.some((p) => ctx.can(p))) return false;
  if (item.superadminOnly && !ctx.canOnboard) return false;
  return isFeatureListed(item.key);
}

export function visibleNavigation(ctx: NavVisibilityContext): NavGroup[] {
  return navigation
    .map((group) => ({ ...group, items: group.items.filter((i) => isNavItemVisible(i, ctx)) }))
    .filter((group) => group.items.length > 0);
}
